import { createSelector } from "reselect";

import { normaliseSourceColumn } from "lib/sideLoadedFieldMapping";

import {
  getSideLoadedBusinesses,
  getSideLoadedDataMappings,
  getSideLoadedDataSets,
} from "./root";

// Exact: a subset or superset match would mis-map a file that only looks familiar.
export const columnSignature = (sourceColumns: string[]): string =>
  (sourceColumns || []).map(normaliseSourceColumn).sort().join(" ");

export const selectSideLoadedDataMappingsList = createSelector(
  [getSideLoadedDataMappings],
  (sideLoadedDataMappings: Record<string, SideLoadedDataMapping>) =>
    Object.values(sideLoadedDataMappings).sort((a, b) =>
      a.name.localeCompare(b.name),
    ),
);

export const selectSideLoadedDataMappingIdByColumnSignatureLookup =
  createSelector(
    [selectSideLoadedDataMappingsList],
    (mappings: SideLoadedDataMapping[]) =>
      mappings.reduce<Record<string, string>>((bySignature, mapping) => {
        // Last wins: the newer mapping over the same columns is the current one.
        bySignature[columnSignature(mapping.sourceColumns)] = mapping.id;
        return bySignature;
      }, {}),
  );

export const selectDataSetCountByMappingIdLookup = createSelector(
  [getSideLoadedDataSets],
  (sideLoadedDataSets: Record<string, SideLoadedDataSet>) =>
    Object.values(sideLoadedDataSets).reduce<Record<string, number>>(
      (counts, dataSet) => {
        if (dataSet.mappingId) {
          counts[dataSet.mappingId] = (counts[dataSet.mappingId] || 0) + 1;
        }
        return counts;
      },
      {},
    ),
);

export const getSideLoadedDataMappingById =
  (mappingId: string) => (state: unknown) =>
    getSideLoadedDataMappings(state)[mappingId] || null;

// Null when the record, data set or mapping has not loaded, or was never mapped.
export const getSideLoadedDataMappingEntriesByRecordId =
  (recordId: string) =>
  (state: unknown): SideLoadedDataMappingEntry[] | null => {
    const record = getSideLoadedBusinesses(state)[recordId];
    const dataSet = record && getSideLoadedDataSets(state)[record.datasetId];
    const mapping =
      dataSet?.mappingId && getSideLoadedDataMappings(state)[dataSet.mappingId];
    return mapping?.entries?.length ? mapping.entries : null;
  };
